import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';
import { AssetSortOrder } from '../../enums/assetSortOrder';
import type {
  AssetDetails,
  AssetFieldValues,
  AssetKey,
  AssetLayerFields,
  AssetListConfig,
  AssetListConfigInput,
  AssetManagerLayer,
  AssetManagerStatus,
  AssetsPage,
  AssetStatistic,
  AssetStatisticInput,
  AssetStatistics,
} from '../../types/InfrastructureManagerTypes';

export const ASSET_LIST_PAGE_SIZE = 50;

// The story's widget limit, used only if the response omits `maxStatistics`.
const DEFAULT_MAX_STATISTICS = 8;
const FIELD_VALUES_LIMIT = 50;

const STATUS_STALE_MS = 5 * 60_000;
// Longer delays overflow setTimeout (~24.8 days), so far-off expiries are re-scheduled daily.
const MAX_EXPIRY_CHECK_MS = 24 * 60 * 60_000;

const msUntilExpiry = (expiresAt?: string | null) => {
  const remaining = expiresAt ? Date.parse(expiresAt) - Date.now() : NaN;
  // Already expired: access is gone and the refetch at the expiry moment has happened, so stop polling.
  if (!Number.isFinite(remaining) || remaining <= 0) return false;
  return Math.min(Math.max(remaining, 1_000), MAX_EXPIRY_CHECK_MS);
};

interface AssetManagerErrorBody {
  error?: string;
  message?: string;
  details?: { field: string; message: string }[];
}

// Validation failures list per-field `details`; everything else carries `message` or `error`.
const getAssetManagerErrorMessage = (error: unknown): string | undefined => {
  const axiosError = error as AxiosError<AssetManagerErrorBody>;
  const body = axiosError?.response?.data;
  return body?.details?.map(({ message }) => message).join(' ') || body?.message || body?.error || axiosError?.message;
};

// 4xx won't change on retry (validation, not found); one retry covers a transient network or server failure.
export const retryUnlessClientError = (failureCount: number, error: unknown) => {
  const status = (error as AxiosError)?.response?.status;
  return !(status && status >= 400 && status < 500) && failureCount < 1;
};

export interface AssetListPageParams {
  configVersion: string | null;
  // An active statistic widget replaces the configured dataset with its own (layer + conditions).
  statisticId: number | null;
  search: string;
  sort: AssetSortOrder;
  pageIndex: number;
}

export interface AssetFieldValuesParams {
  layerId: number;
  field: string;
  search: string;
}

const AssetManagerService = () => {
  // Re-checked on focus once stale, and again the moment the add-on expires, so an open tab loses access on time.
  // Keyed on the CES Admin's selected client so a client switch never reads the previous client's status.
  const getStatus = (clientId?: number): UseQueryOptions<AssetManagerStatus, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_MANAGER_STATUS, clientId],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_STATUS, { signal });
      return { enabled: !!data.enabled, assetMapId: data.assetMap?.id ?? null, expiresAt: data.addon?.expiresAt ?? null };
    },
    staleTime: STATUS_STALE_MS,
    refetchInterval: (query) => msUntilExpiry(query.state.data?.expiresAt),
    retry: retryUnlessClientError,
  });

  const getMetaLayers = (): UseQueryOptions<AssetManagerLayer[], Error> => ({
    queryKey: [QueryKeys.GET_ASSET_MANAGER_META_LAYERS],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_META_LAYERS, { signal });
      return data.layers;
    },
    staleTime: 5 * 60_000,
    retry: retryUnlessClientError,
  });

  const getLayerFields = (layerId: number): UseQueryOptions<AssetLayerFields, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_MANAGER_LAYER_FIELDS, layerId],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_LAYER_FIELDS.replace(':layerId', String(layerId)), { signal });
      return { fields: data.fields, suggestedKeyField: data.suggestedKeyField ?? null };
    },
    staleTime: 5 * 60_000,
    retry: retryUnlessClientError,
  });

  const setLayerKeyField = (): UseMutationOptions<void, Error, { layerId: number; keyField: string }> => ({
    mutationKey: [MutationKeys.SET_ASSET_LAYER_KEY_FIELD],
    mutationFn: async ({ layerId, keyField }) => {
      await axiosInstance.put(ApiRoutes.ASSET_MANAGER_LAYER_KEY_FIELD.replace(':layerId', String(layerId)), { keyField });
    },
  });

  const getListConfig = (): UseQueryOptions<AssetListConfig, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_LIST_CONFIG],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_LIST_CONFIG, { signal });
      return { rows: data.rows, updatedAt: data.updatedAt };
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: retryUnlessClientError,
  });

  const saveListConfig = (): UseMutationOptions<AssetListConfig, Error, AssetListConfigInput[]> => ({
    mutationKey: [MutationKeys.SAVE_ASSET_LIST_CONFIG],
    mutationFn: async (rows) => {
      const { data } = await axiosInstance.put(ApiRoutes.ASSET_MANAGER_LIST_CONFIG, { rows });
      return { rows: data.rows, updatedAt: data.updatedAt };
    },
  });

  // Short gcTime: pages scrolled out of view are released, not accumulated.
  const getAssetsPage = ({ configVersion, statisticId, search, sort, pageIndex }: AssetListPageParams): UseQueryOptions<AssetsPage, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_LIST_PAGE, configVersion, statisticId, search, sort, pageIndex],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_ASSETS, {
        params: { search, sort, statisticId: statisticId ?? undefined, limit: ASSET_LIST_PAGE_SIZE, offset: pageIndex * ASSET_LIST_PAGE_SIZE },
        signal,
      });
      return { assets: data.assets, total: data.pagination.total };
    },
    staleTime: 60_000,
    gcTime: 30_000,
    retry: retryUnlessClientError,
  });

  // Short gcTime: freed soon after its row collapses or scrolls away.
  const getAssetDetails = ({ layerId, keyValue }: AssetKey): UseQueryOptions<AssetDetails, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_DETAILS, layerId, keyValue],
    queryFn: async ({ signal }) => {
      const endpoint = ApiRoutes.ASSET_MANAGER_ASSET_DETAILS.replace(':layerId', String(layerId)).replace(':keyValue', encodeURIComponent(keyValue));
      const { data } = await axiosInstance.get(endpoint, { signal });
      return data;
    },
    staleTime: 30_000,
    gcTime: 15_000,
    retry: retryUnlessClientError,
  });

  // Short gcTime: only held while a WHERE value dropdown is open.
  const getFieldValues = ({ layerId, field, search }: AssetFieldValuesParams): UseQueryOptions<AssetFieldValues, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_LAYER_FIELD_VALUES, layerId, field, search],
    queryFn: async ({ signal }) => {
      const endpoint = ApiRoutes.ASSET_MANAGER_LAYER_FIELD_VALUES.replace(':layerId', String(layerId)).replace(':field', encodeURIComponent(field));
      const { data } = await axiosInstance.get(endpoint, { params: { search: search || undefined, limit: FIELD_VALUES_LIMIT }, signal });
      return { values: data.values, truncated: data.truncated };
    },
    staleTime: 30_000,
    gcTime: 30_000,
    retry: retryUnlessClientError,
  });

  // Values are computed on read; each item carries its full config, so editing needs no extra fetch.
  const getStatistics = (): UseQueryOptions<AssetStatistics, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_STATISTICS],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_STATISTICS, { signal });
      return { statistics: data.statistics, maxStatistics: data.maxStatistics ?? DEFAULT_MAX_STATISTICS };
    },
    staleTime: 60_000,
    retry: retryUnlessClientError,
  });

  const createStatistic = (): UseMutationOptions<AssetStatistic, Error, AssetStatisticInput> => ({
    mutationKey: [MutationKeys.CREATE_ASSET_STATISTIC],
    mutationFn: async (input) => {
      const { data } = await axiosInstance.post(ApiRoutes.ASSET_MANAGER_STATISTICS, input);
      return data;
    },
  });

  const updateStatistic = (): UseMutationOptions<AssetStatistic, Error, { id: number; input: AssetStatisticInput }> => ({
    mutationKey: [MutationKeys.UPDATE_ASSET_STATISTIC],
    mutationFn: async ({ id, input }) => {
      const { data } = await axiosInstance.put(ApiRoutes.ASSET_MANAGER_STATISTIC_BY_ID.replace(':id', String(id)), input);
      return data;
    },
  });

  const deleteStatistic = (): UseMutationOptions<void, Error, number> => ({
    mutationKey: [MutationKeys.DELETE_ASSET_STATISTIC],
    mutationFn: async (id) => {
      await axiosInstance.delete(ApiRoutes.ASSET_MANAGER_STATISTIC_BY_ID.replace(':id', String(id)));
    },
  });

  return {
    getStatus,
    getMetaLayers,
    getLayerFields,
    setLayerKeyField,
    getFieldValues,
    getListConfig,
    saveListConfig,
    getAssetsPage,
    getAssetDetails,
    getStatistics,
    createStatistic,
    updateStatistic,
    deleteStatistic,
  };
};

export default AssetManagerService;
export { getAssetManagerErrorMessage };
